import { collectBootstrapDiagnostics, serialiseBootstrapDiagnostics } from './bootstrap-diagnostics.util';
import {
    getChunkLoadIncident,
    isChunkLoadError,
    isStorageBlockedError,
    reloadOnceForChunkError,
    showChunkLoadErrorPage,
    showStorageBlockedErrorPage,
} from './chunk-load-error.util';
import { SpecifierResolver } from './module-map-probe.util';

// How each category is logged, and how to find incidents that didn't recover: client-error.router.ts
export interface ClientErrorReport {
    category: 'storage_blocked' | 'bootstrap_retry' | 'recovered';
    message: string;
    stack?: string;
    source: string;
    // The chunk-load incident's ID - see getChunkLoadIncident
    incidentId?: number;
    msToRecover?: number;
    errorKind?: 'chunk_load' | 'unknown';
    // Pre-serialised JSON from bootstrap-diagnostics.util.ts
    diagnostics?: string;
}

/** Plain fetch rather than the App Insights SDK, since these fire before Angular (and the SDK) has started. */
export function reportClientError(report: ClientErrorReport): void {
    fetch('/monitoring/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(report),
        // Survives a reload straight after the report
        keepalive: true,
    }).catch(() => {
        // Telemetry is best-effort; never interfere with the error UI
    });
}

/**
 * main-mfe.ts's handler for a failed federation init or bootstrap import. `resolveBare` resolves a
 * bare specifier through the page's import map - see collectBootstrapDiagnostics.
 */
export function handleBootstrapFailure(err: unknown, resolveBare: SpecifierResolver): void {
    console.error(err);
    const message = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;

    // A blocked storage partition (e.g. Safari's "Block all cookies") isn't a deployment problem -
    // reloading can never fix a browser privacy setting, so skip the reload guard entirely and go
    // straight to a page that actually explains what's wrong and how to fix it.
    if (isStorageBlockedError(err)) {
        reportClientError({ category: 'storage_blocked', message, stack, source: 'main-mfe' });
        showStorageBlockedErrorPage();
        return;
    }

    // Bootstrap failures here are usually a stale importmap.json/remoteEntry.json caught
    // mid-deployment - one automatic reload self-heals most of them. Only fall through to the
    // manual error UI below if we already tried that once this session.
    if (reloadOnceForChunkError()) {
        return;
    }

    // The reload didn't fix it. Report the incident once, when the error page first appears - a
    // later recovered report (chunk-load-recovery.ts), or the lack of one, says how it ended.
    // Deliberately don't clear the guard here: it stays tripped (via the URL) so the incident
    // carries through every retry. It's cleared only once the app has loaded.
    const isNewIncident = !getChunkLoadIncident()?.id;
    showChunkLoadErrorPage();
    if (!isNewIncident) {
        return;
    }

    // Only chunk failures get diagnostics - the browser's error text says nothing about why the
    // fetch failed. Collected in the background so the error page isn't held up by the probes.
    const isChunkError = isChunkLoadError(err);
    const diagnostics = isChunkError
        ? collectBootstrapDiagnostics(err, resolveBare)
              .then(serialiseBootstrapDiagnostics)
              .catch((diagError: unknown) => JSON.stringify({ collectionError: String(diagError) }))
        : Promise.resolve(undefined);

    diagnostics.then((diagnosticsJson) =>
        reportClientError({
            category: 'bootstrap_retry',
            message,
            stack,
            source: 'main-mfe',
            incidentId: getChunkLoadIncident()?.id ?? undefined,
            errorKind: isChunkError ? 'chunk_load' : 'unknown',
            diagnostics: diagnosticsJson,
        })
    );
}
